import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getAllPartenaires } from "../../services/partenairesServices";

const FALLBACK = "/img/no-image.jpg";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

// ✅ /uploads/... => absolu API (Vite + CRA), sinon laisse passer https://, /img/...
function toAbsolute(u) {
  if (!u) return "";
  const s = String(u).trim();

  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;

  if (!s.startsWith("/uploads/") && !s.startsWith("/uploads")) return s;

  const rawBase =
    (typeof import.meta !== "undefined" &&
      import.meta.env &&
      import.meta.env.VITE_API_URL) ||
    process.env.REACT_APP_API_URL ||
    "";

  const base = String(rawBase).replace(/\/$/, "");
  return base ? `${base}${s}` : s;
}

const TousLesPartenaires = () => {
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartenaires = async () => {
      setLoading(true);
      try {
        const all = await getAllPartenaires();
        const list = Array.isArray(all) ? all : [];
        const actifs = list.filter((p) => p?.is_active);
        // optionnel : tri par ordre si tu l'utilises
        actifs.sort((a, b) => Number(a.ordre || 0) - Number(b.ordre || 0));
        setPartenaires(actifs);
      } catch (err) {
        console.error("Erreur chargement partenaires :", err);
        setPartenaires([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPartenaires();
  }, []);

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  const count = partenaires.length;

  return (
    <section className="mt-[150px]">
      {/* Hero / Header */}
      <div className="mx-auto max-w-[1280px] px-4">
        <div className="rounded-3xl overflow-hidden border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 backdrop-blur shadow-sm">
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-6 py-8 sm:px-8">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
              Tous nos partenaires
            </h1>
            <p className="mt-2 text-white/90 text-sm sm:text-[15px]">
              Ils soutiennent le club — merci à eux.
            </p>
          </div>

          <div className="p-4 sm:p-6">
            {/* Loading */}
            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4"
                  >
                    <div className="h-28 rounded-xl bg-slate-200 dark:bg-slate-700" />
                    <div className="mt-4 h-4 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />
                    <div className="mt-2 h-3 w-1/2 rounded bg-slate-200 dark:bg-slate-700" />
                    <div className="mt-4 h-9 w-full rounded-xl bg-slate-200 dark:bg-slate-700" />
                  </div>
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && count === 0 && (
              <div className="py-14 text-center">
                <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Aucun partenaire pour le moment
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Revenez plus tard 🙂
                </p>
              </div>
            )}

            {/* Grid */}
            {!loading && count > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {partenaires.map((p) => {
                  const logo = toAbsolute(p.photo_url) || FALLBACK;
                  const hasDetail = Boolean(p.is_detail);
                  const hasLink = Boolean(p.link_url);

                  return (
                    <div
                      key={p.id}
                      className={cn(
                        "group relative rounded-3xl overflow-hidden",
                        "border border-slate-200/70 dark:border-slate-800",
                        "bg-white/80 dark:bg-slate-900/50 backdrop-blur",
                        "shadow-sm transition duration-300",
                        "hover:shadow-lg hover:-translate-y-[2px]",
                      )}
                    >
                      {/* mini accent */}
                      <div
                        aria-hidden
                        className="h-1.5 w-full opacity-70"
                        style={{
                          background:
                            "linear-gradient(to right, transparent, var(--tw-color-primary, #2563eb), transparent)",
                        }}
                      />

                      {/* badge */}
                      {(hasLink || hasDetail) && (
                        <div className="absolute right-4 top-4 z-10">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold",
                              "bg-white/85 dark:bg-slate-950/60 backdrop-blur",
                              "border border-slate-200/70 dark:border-slate-800",
                              "text-slate-800 dark:text-slate-100",
                            )}
                          >
                            {hasDetail ? "Détails" : "Site"}
                          </span>
                        </div>
                      )}

                      {/* logo */}
                      <div className="p-5">
                        <div
                          className={cn(
                            "relative rounded-2xl grid place-items-center overflow-hidden",
                            "bg-slate-50 dark:bg-slate-950/30",
                            "border border-slate-200/60 dark:border-slate-800/60",
                            "h-32 sm:h-36",
                            "transition group-hover:border-primary/40",
                          )}
                        >
                          <img
                            src={logo}
                            alt={p.nom}
                            onError={onImgError}
                            loading="lazy"
                            decoding="async"
                            className="max-h-24 w-auto object-contain transition duration-300 group-hover:scale-[1.03]"
                          />
                          <span
                            aria-hidden
                            className="pointer-events-none absolute -inset-10 opacity-0 group-hover:opacity-100 transition"
                            style={{
                              background:
                                "radial-gradient(circle at 50% 60%, rgba(59,130,246,.18), transparent 55%)",
                            }}
                          />
                        </div>

                        <h2 className="mt-4 text-base font-extrabold tracking-tight text-center text-slate-900 dark:text-white line-clamp-2">
                          {p.nom}
                        </h2>

                        {/* actions */}
                        <div className="mt-4 flex flex-col gap-2">
                          {hasLink ? (
                            <a
                              href={p.link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={cn(
                                "inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold",
                                "bg-blue-600 text-white hover:bg-blue-700 transition",
                              )}
                              aria-label={`Ouvrir le site de ${p.nom}`}
                            >
                              Ouvrir le site
                            </a>
                          ) : (
                            <div className="text-center text-xs text-slate-500 dark:text-slate-400 py-2">
                              Aucun site renseigné
                            </div>
                          )}

                          {hasDetail && (
                            <Link
                              to={`/partenaires/${encodeURIComponent(p.nom)}`}
                              className={cn(
                                "inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold",
                                "bg-primary text-white hover:opacity-90 transition",
                              )}
                              aria-label={`Voir les détails de ${p.nom}`}
                            >
                              Voir la fiche
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TousLesPartenaires;
